import React, { useMemo, useState } from 'react';
import { Check, ChevronDown, Clipboard, Code2, Info, ListTree, User } from 'lucide-react';
import { PanelEvent } from '../../lib/panelTypes';
import { countryName } from '../../lib/country';

interface Props {
  event: PanelEvent;
  onFilterUser?: (user: string) => void;
  onClose: () => void;
  // Read-only 'insights' accounts: the API strips identities and the raw
  // payload, so show only the fields that still arrive.
  limited?: boolean;
}

type DetailTab = 'properties' | 'context' | 'json';
type PropertyEntry = { name: string; value: unknown };

const TECHNICAL_KEYS = new Set([
  '_id',
  '__v',
  'deletedAt',
  'deletedBy',
  'timestamp',
  'event_name',
  'eventType',
  'source',
  'userId',
  'anonymous_id',
  'user_id',
  'platform',
  'app_version',
  'appVersion',
  'build_number',
  'buildNumber',
  'device_info',
  'deviceInfo',
  'environment',
  // Shown under Context.
  'ip_country',
  'device_region',
  'device_locale',
  'browser_language',
]);

const flattenProperties = (value: unknown, prefix = '', depth = 0): PropertyEntry[] => {
  if (!value || typeof value !== 'object' || Array.isArray(value) || depth >= 3) {
    return prefix ? [{ name: prefix, value }] : [];
  }

  return Object.entries(value as Record<string, unknown>).flatMap(([key, child]) => {
    if (!prefix && TECHNICAL_KEYS.has(key)) return [];
    const path = prefix ? `${prefix}.${key}` : key;
    if (child && typeof child === 'object' && !Array.isArray(child)) {
      return flattenProperties(child, path, depth + 1);
    }
    return [{ name: path, value: child }];
  });
};

const looksLikeDate = (key: string, value: string): boolean =>
  /(time|timestamp|_at|date)$/i.test(key) && !Number.isNaN(Date.parse(value));

const displayDate = (value: string): string =>
  new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Copenhagen',
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));

const PropertyValue: React.FC<PropertyEntry> = ({ name, value }) => {
  if (value === null || value === undefined || value === '') {
    return <span className="font-semibold text-gray-400">&lt;null&gt;</span>;
  }

  const text = typeof value === 'number'
    ? value.toLocaleString()
    : typeof value === 'string'
      ? value
      : typeof value === 'boolean'
        ? String(value)
        : JSON.stringify(value);
  return (
    <span className="min-w-0 break-words font-semibold text-gray-700">
      {typeof value === 'string' && looksLikeDate(name, value) ? displayDate(value) : text}
    </span>
  );
};

const ReadableFields: React.FC<{ entries: PropertyEntry[] }> = ({ entries }) => (
  <div className="columns-1 gap-x-10 md:columns-2 xl:columns-3">
    {entries.map((entry) => (
      <div
        key={entry.name}
        className="flex min-h-[52px] break-inside-avoid items-start gap-2 border-b border-gray-200 px-2 py-3 text-sm leading-6"
      >
        <span className="max-w-[55%] shrink-0 break-words text-gray-500">{entry.name}:</span>
        <PropertyValue {...entry} />
      </div>
    ))}
  </div>
);

const CopyButton: React.FC<{ value: string; label: string }> = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-800"
      aria-label={`Copy ${label}`}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Clipboard className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const EventDetails: React.FC<Props> = ({ event, onFilterUser, onClose, limited = false }) => {
  const [tab, setTab] = useState<DetailTab>(limited ? 'context' : 'properties');
  const identity = event.identity || {
    anonymous_id: event.user || null,
    user_id: null,
    kind: event.source === 'web_events' ? 'website' : 'app',
  };
  const context = event.context || {
    city: null,
    platform: null,
    os_version: null,
    app_version: null,
    build_number: null,
    device_info: null,
    environment: null,
    lot_id: null,
    session_id: null,
  };
  const withCode = (code?: string | null) => (code ? `${countryName(code)} (${code})` : null);
  const propertyEntries = useMemo(
    () => flattenProperties(event.properties).sort((a, b) => a.name.localeCompare(b.name)),
    [event.properties]
  );
  const exactTime = displayDate(event.ts);
  const visitor = event.user;
  const os = [context.platform, context.os_version].filter(Boolean).join(' ');
  const limitedContextEntries: PropertyEntry[] = [
    { name: 'City', value: context.city },
    { name: 'IP country', value: withCode(context.ip_country) },
    ...(identity.kind === 'website' ? [] : [{ name: 'Phone region', value: withCode(context.device_region) }]),
    { name: 'Platform', value: os || null },
    { name: 'App version', value: context.app_version },
    { name: 'Build number', value: context.build_number },
    { name: 'Environment', value: context.environment },
    { name: 'Parking lot ID', value: context.lot_id },
    { name: 'Technical source', value: event.source },
  ];
  const contextEntries: PropertyEntry[] = limited ? limitedContextEntries : [
    { name: 'Visitor/device ID', value: identity.anonymous_id },
    { name: 'Linked account ID', value: identity.user_id },
    { name: 'Identity type', value: identity.kind === 'website' ? 'Website visitor' : 'App visitor/device' },
    { name: 'City', value: context.city },
    { name: 'IP country', value: withCode(context.ip_country) },
    ...(identity.kind === 'website'
      ? [{ name: 'Browser language', value: context.browser_language }]
      : [
          { name: 'Phone region', value: withCode(context.device_region) },
          { name: 'Phone language', value: context.device_locale },
        ]),
    { name: 'OS', value: os || null },
    { name: 'Device', value: context.device_info },
    { name: 'App version', value: context.app_version },
    { name: 'Build number', value: context.build_number },
    { name: 'Environment', value: context.environment },
    { name: 'Parking lot ID', value: context.lot_id },
    { name: 'Session ID', value: context.session_id },
    { name: 'Technical source', value: event.source },
  ];
  const tabs: Array<{ key: DetailTab; label: string; icon: React.ElementType }> = [
    ...(limited ? [] : [{ key: 'properties' as DetailTab, label: `Event properties (${propertyEntries.length})`, icon: ListTree }]),
    { key: 'context', label: 'Context', icon: Info },
    { key: 'json', label: 'JSON', icon: Code2 },
  ];

  return (
    <div className="border-y border-indigo-100 bg-white px-5 py-4">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-2 text-left font-semibold text-gray-900 hover:text-indigo-700"
            aria-label={`Collapse ${event.event_name}`}
          >
            <ChevronDown className="h-4 w-4 text-indigo-500" /> {event.event_name}
          </button>
          <div className="mt-1 text-xs text-gray-500">{exactTime} · Europe/Copenhagen</div>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {visitor && onFilterUser && (
            <>
              <button
                type="button"
                onClick={() => onFilterUser(visitor)}
                className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-white px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50"
              >
                <User className="h-3.5 w-3.5" /> View visitor journey
              </button>
              <CopyButton value={visitor} label="visitor ID" />
            </>
          )}
        </div>
      </div>

      <div className="mb-4 flex gap-1 border-b border-gray-200" role="tablist" aria-label="Event details">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium ${
              tab === key
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>

      {tab === 'properties' && (
        propertyEntries.length ? (
          <ReadableFields entries={propertyEntries} />
        ) : (
          <div className="rounded-md border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
            This event has no custom properties.
          </div>
        )
      )}

      {tab === 'context' && (
        <ReadableFields entries={contextEntries} />
      )}

      {tab === 'json' && (
        <div className="relative">
          <div className="absolute right-2 top-2">
            <CopyButton value={JSON.stringify(event, null, 2)} label="event JSON" />
          </div>
          <pre className="max-h-[440px] overflow-auto rounded-md border bg-gray-950 p-4 pr-20 text-xs leading-relaxed text-gray-100">
            {JSON.stringify(event, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default EventDetails;
